import React from 'react';
import { RefreshCw, Percent, Repeat } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Опции для конвертации типов
const conversionOptions = [
  { 
    id: 'percent', 
    name: 'Проценты (%)', 
    description: 'Стандартное представление доли в процентах' 
  },
  { 
    id: 'decimal', 
    name: 'Десятичная дробь', 
    description: 'Число от 0 до 1, например, 0.1 = 10%' 
  },
  { 
    id: 'fraction', 
    name: 'Обыкновенная дробь', 
    description: 'Представление в виде дроби, например, 1/10 = 10%' 
  }
];

const CalculationForm = ({ 
  formData, 
  setFormData, 
  calculateConversion, 
  isCalculating, 
  errors 
}) => {
  // Обработчик изменения значения
  const handleValueChange = (e) => {
    const newValue = e.target.value.replace(',', '.');
    setFormData(prev => ({ ...prev, value: newValue }));
  };

  // Обработчик смены типа конвертации
  const handleTypeChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Смена мест типов конвертации
  const swapConversionTypes = () => {
    setFormData(prev => ({
      value: prev.value,
      fromType: prev.toType,
      toType: prev.fromType
    }));
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); calculateConversion(); }} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Блок конвертации */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Значение для конвертации */}
          <div>
            <label htmlFor="value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Percent className="w-4 h-4 mr-2 text-green-500" />
              Значение для конвертации
            </label>
            <input
              type="text"
              id="value"
              name="value"
              value={formData.value}
              onChange={handleValueChange}
              className={`form-input w-full ${errors.value ? 'input-error' : ''}`}
              placeholder="Введите число"
            />
            {errors.value && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.value}
              </p>
            )}
          </div>

          {/* Кнопка смены мест */}
          <div className="flex items-end justify-center md:col-span-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={swapConversionTypes}
              className="flex items-center gap-2"
              aria-label="Поменять местами типы конвертации"
            >
              <Repeat className="w-4 h-4" />
              Поменять местами
            </Button>
          </div>

          {/* Исходный тип */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Из
            </label>
            <div className="grid grid-cols-1 gap-2">
              {conversionOptions.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleTypeChange('fromType', option.id)}
                  className={`p-2 text-sm rounded-lg border transition-colors text-left
                    ${formData.fromType === option.id 
                      ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  <div className="font-medium">{option.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Целевой тип */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              В
            </label>
            <div className="grid grid-cols-1 gap-2">
              {conversionOptions.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleTypeChange('toType', option.id)}
                  className={`p-2 text-sm rounded-lg border transition-colors text-left
                    ${formData.toType === option.id 
                      ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  <div className="font-medium">{option.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<RefreshCw className="w-5 h-5" />}
            disabled={isCalculating}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Конвертируем...' : 'Конвертировать'}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CalculationForm;